import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { untranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { AuthService } from 'services/auth.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { CommonDbService } from 'services/common-db.service';
import { ConfigDbService } from 'services/config-db.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { DatabaseWrapper } from 'services/database-wrapper';
import { PouchDoc } from '@cf-shared/data-model/pouch';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { ToastController } from 'providers/toast-controller';
import { CheckUserExistsResult } from '@cf-shared/check-user-exists-interface';
import { describeErrorAndReportIfUnexpected } from 'util/version-mismatch-error-policy';
import { USER_LIST_CONFIG_KEYS } from 'services/config-keys';
import { switchedOnUsers } from 'services/config-record-resolve';

interface ConfigRecord extends PouchDoc {
  globalValue: any;
  userValues: { [userId: string]: any };
}

interface UserOverride {
  userId: string;
  value: any;
}

@Component({
    selector: 'page-admin-config',
    templateUrl: 'admin-config.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class AdminConfigPage extends CsPage<{}> {
  configs: ConfigRecord[] = [];
  userEmails: { [uid: string]: string } = {};

  constructor(
    public navCtrl: NavController,
    private auth: AuthService,
    private commonDb: CommonDbService,
    private configDb: ConfigDbService,
    private cloudFunctions: CloudFunctionsService,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Dev);
  }

  async ionViewWillEnter() {
    await this.commonDb.initializeAsync();
    this.configs = await this.commonDb.database.withDocWatcherAsync(
      watcher => this.commonDb.database.getByIdHeadAsync('config/', watcher)) as ConfigRecord[];
    this.cd.markForCheck();

    // resolve UIDs to emails for display
    const allUids = new Set<string>();
    for (const config of this.configs) {
      if (config.userValues) {
        for (const uid of Object.keys(config.userValues)) {
          allUids.add(uid);
        }
      }
    }
    if (allUids.size > 0) {
      const result = await this.cloudFunctions.resolveUserEmailsAsync({ uids: [...allUids] });
      if (this.viewDestroyed) return;
      this.userEmails = result.emails;
      this.cd.markForCheck();
    }
  }

  getConfigKey(config: ConfigRecord): string {
    return config._id.replace(/^config\//, '');
  }

  getUserDisplayLabel(userId: string): string {
    return this.userEmails[userId] || userId;
  }

  // a key switched on one user at a time, shown as a plain list of users with no
  // global value; see USER_LIST_CONFIG_KEYS for why these keys have none
  isUserListKey(config: ConfigRecord): boolean {
    return (USER_LIST_CONFIG_KEYS as readonly string[]).includes(this.getConfigKey(config));
  }

  getUserOverrides(config: ConfigRecord): UserOverride[] {
    // on a user-list key list exactly the users switched on; anything else stored
    // there is dropped on the next save
    const userValues = this.isUserListKey(config) ? switchedOnUsers(config.userValues) : config.userValues;
    if (!userValues) return [];
    return Object.entries(userValues).map(([userId, value]) => ({ userId, value }));
  }

  isBoolean(value: any): boolean {
    return typeof value === 'boolean';
  }

  toggleGlobalValue(config: ConfigRecord) {
    config.globalValue = !config.globalValue;
    this.cd.markForCheck();
  }

  onGlobalValueInput(config: ConfigRecord, event: Event) {
    const input = event.target as HTMLInputElement;
    config.globalValue = this.parseValue(input.value);
  }

  toggleUserValue(config: ConfigRecord, userId: string) {
    config.userValues[userId] = !config.userValues[userId];
    this.cd.markForCheck();
  }

  onUserValueInput(config: ConfigRecord, userId: string, event: Event) {
    const input = event.target as HTMLInputElement;
    config.userValues[userId] = this.parseValue(input.value);
  }

  async addUserOverrideAsync(config: ConfigRecord) {
    const email = await this.alertCtrl.showAlertAsync<string>({
      title: untranslatedString('Add User Override'),
      message: untranslatedString('Enter the user\'s email:'),
      inputs: [{ name: 'email', type: 'email', placeholder: untranslatedString('user@example.com') }],
      buttons: [
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel, result: null },
        { text: untranslatedString('Add'), role: AlertButtonRole.Primary, result: (data: any) => data.email },
      ],
    });
    if (!email) return;

    let result: CheckUserExistsResult;
    try {
      result = await this.cloudFunctions.checkUserExistsAsync({ email });
    } catch (err) {
      // checkUserExists can THROW, not only answer yes/no. Note WHICH doc is
      // involved: the looked-up email is resolved in Firebase Auth, and the only
      // `_users` read is of the CALLING officer's own role doc — so since #4283
      // this refuses (HTTP 409) when the OFFICER's doc is newer than the live
      // server understands, not the searched account's. With no catch here the
      // whole "Add User Override" flow ended in silence — no override added and
      // nothing said why, which reads exactly like the button is broken.
      // shared policy (wording plus what reaches Sentry): see the helper.
      await this.alertCtrl.showAlertAsync({
        title: untranslatedString('Could Not Look Up User'),
        message: describeErrorAndReportIfUnexpected(
          err,
          this.auth.logging,
          untranslatedString(`Could not check whether "${email}" exists: ${err?.message ?? err}`),
        ),
        buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary, result: null }],
      });
      return;
    }
    // several Firebase accounts hold this address, so the server would not name
    // one (#4505). Say that, rather than the "not found" message below — the
    // accounts DO exist, and sending the officer looking for a missing account
    // is exactly the wrong-diagnosis problem that ticket exists to remove.
    if (result.ambiguous) {
      await this.alertCtrl.showAlertAsync({
        title: untranslatedString('Several Accounts On That Email'),
        message: untranslatedString(
          `More than one Firebase account uses "${email}", so the server could not tell which one you mean. `
          + `The account ids are in the server log. Add the override by looking up the right account another way.`),
        buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary, result: null }],
      });
      return;
    }
    if (!result.exists || !result.uid) {
      await this.alertCtrl.showAlertAsync({
        title: untranslatedString('User Not Found'),
        message: untranslatedString(`No Firebase user found for "${email}".`),
        buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary, result: null }],
      });
      return;
    }

    if (!config.userValues) config.userValues = {};
    // on a user-list key, adding the user IS switching them on; elsewhere the new
    // override starts at the global value for the operator to change
    config.userValues[result.uid] = this.isUserListKey(config) ? true : config.globalValue;
    this.userEmails[result.uid] = email;
    this.cd.markForCheck();
  }

  removeUserOverride(config: ConfigRecord, userId: string) {
    delete config.userValues[userId];
    this.cd.markForCheck();
  }

  async addConfigAsync() {
    const result = await this.alertCtrl.showAlertAsync<{ key: string; type: string }>({
      title: untranslatedString('New Config Variable'),
      inputs: [
        { name: 'key', type: 'text', placeholder: untranslatedString('ConfigKeyName'), label: untranslatedString('Key') },
        {
          name: 'type', type: 'select', label: untranslatedString('Type'), value: 'boolean',
          selectOptions: [
            { label: untranslatedString('Boolean'), value: 'boolean' },
            { label: untranslatedString('Number'), value: 'number' },
            { label: untranslatedString('String'), value: 'string' },
          ],
        },
      ],
      buttons: [
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel, result: null },
        { text: untranslatedString('Create'), role: AlertButtonRole.Primary, result: (data: any) => data },
      ],
    });
    if (!result?.key) return;

    const defaultValues: Record<string, any> = { boolean: false, number: 0, string: '' };
    const newConfig = { _id: `config/${result.key}`, globalValue: defaultValues[result.type], userValues: {} } as ConfigRecord;
    await this.commonDb.database.saveRecordAsync(newConfig, DatabaseWrapper.getTransactionId());
    // local writes don't flow through the sync-pull change feed, so push the new value
    // into ConfigDbService directly — otherwise it won't take effect until the next reload
    this.configDb.applyLocalConfigWrite(newConfig);
    this.configs = [...this.configs, newConfig];
    this.cd.markForCheck();
  }

  handleSave(event: Event, config: ConfigRecord) {
    lockClick(event, this.saveAsync(config));
  }

  handleDelete(event: Event, config: ConfigRecord) {
    lockClick(event, this.deleteAsync(config));
  }

  private async saveAsync(config: ConfigRecord) {
    // if this is a user-list key, store it in its one valid shape: global value off,
    // and only the users switched on. This also replaces the old list-of-user-ids
    // global value these keys used to hold.
    if (this.isUserListKey(config)) {
      config.globalValue = false;
      config.userValues = switchedOnUsers(config.userValues);
    }
    await this.commonDb.database.saveRecordAsync(config, DatabaseWrapper.getTransactionId());
    // local writes don't flow through the sync-pull change feed, so push the new value
    // into ConfigDbService directly — otherwise it won't take effect until the next reload
    this.configDb.applyLocalConfigWrite(config);
    this.toastCtrl.showToast({ message: untranslatedString('Config saved'), duration: 2000 });
  }

  private async deleteAsync(config: ConfigRecord) {
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: untranslatedString('Delete Config'),
      message: untranslatedString(`Delete "${this.getConfigKey(config)}"?`),
      buttons: [
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel, result: false },
        { text: untranslatedString('Delete'), role: AlertButtonRole.Primary, result: true },
      ],
    });
    if (!confirmed) return;

    await this.commonDb.database.deleteRecordAsync(config, DatabaseWrapper.getTransactionId());
    this.configs = this.configs.filter(c => c !== config);
    this.cd.markForCheck();
  }

  private parseValue(raw: string): any {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    const num = Number(raw);
    if (!isNaN(num) && raw.trim() !== '') return num;
    return raw;
  }
}
